import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import Button from "@mui/joy/Button";
import {keyframes} from "@mui/system";
import SmartToyRoundedIcon from "@mui/icons-material/SmartToyRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import GeneralApiService from "../../../axios/Axios";

export type RonaldTask = {
    id: number;
    status: string;
    task_type: string;
    channel: string;
    planned_content?: string;
    follow_up_hint?: string;
    scheduled_at?: string | null;
    cancelled_at?: string | null;
    cancel_reason?: string;
    needs_human_intervention: boolean;
    intervention_reason?: string;
    intervention_acknowledged: boolean;
    action_taken?: string;
    pending_action?: { action: string; reason: string } | null;
    updated_at?: string;
    created_at?: string;
};

export type RonaldStatus = {
    customer_id: number;
    franchise_id: number | null;
    scope: "booking" | "lead" | "none";
    ai_responses_enabled: boolean;
    customer_details?: { unsubscribed?: boolean; opt_out?: boolean };
    active_lead: { id: number; ai_responses_enabled: boolean } | null;
    active_booking: { id: number; ai_responses_enabled: boolean } | null;
    pending_tasks: RonaldTask[];
    intervention_tasks: RonaldTask[];
    cancelled_tasks?: RonaldTask[];
    needs_human_intervention: boolean;
};

interface RonaldStatusStripProps {
    customerId: string | number | undefined;
    refreshKey?: number;
    onStatusChange?: (status: RonaldStatus | null) => void;
}

/**
 * Invisible polling engine. Keeps the Ronald status up-to-date and pushes
 * every update to the parent via `onStatusChange`. Renders nothing — the
 * pending bubble, follow-up cards, audit cards, etc. are all rendered by
 * the parent (MessagesPane) using the status it receives.
 */
export default function RonaldStatusStrip({customerId, refreshKey, onStatusChange}: RonaldStatusStripProps) {
    const [status, setStatus] = useState<RonaldStatus | null>(null);
    const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const onStatusChangeRef = useRef(onStatusChange);
    onStatusChangeRef.current = onStatusChange;

    const fetchStatus = useCallback(async () => {
        if (!customerId) return;
        try {
            const data = await GeneralApiService.getRonaldCustomerStatus(customerId);
            setStatus(data);
            onStatusChangeRef.current?.(data);
        } catch (err) {
            console.debug("Ronald status fetch failed", err);
        }
    }, [customerId]);

    useEffect(() => {
        if (!customerId) {
            setStatus(null);
            onStatusChangeRef.current?.(null);
            return;
        }
        fetchStatus();
    }, [customerId, fetchStatus, refreshKey]);

    // Poll: 2s when something is pending, 15s when idle.
    useEffect(() => {
        if (!customerId) return undefined;
        const hasPending = (status?.pending_tasks?.length ?? 0) > 0;
        const hasIntervention = (status?.intervention_tasks?.length ?? 0) > 0;
        const delay = hasPending || hasIntervention ? 2000 : 15000;
        pollRef.current = setTimeout(fetchStatus, delay);
        return () => {
            if (pollRef.current) clearTimeout(pollRef.current);
        };
    }, [customerId, status, fetchStatus]);

    return null;
}


// ---------------------------------------------------------------------------
// Exported components for use by MessagesPane inside the timeline
// ---------------------------------------------------------------------------

const bounce = keyframes`
    0%, 80%, 100% { transform: translateY(0); }
    40% { transform: translateY(-6px); }
`;

function useCountdown(targetDate: string | null): string {
    const [label, setLabel] = useState("");
    useEffect(() => {
        if (!targetDate) { setLabel(""); return undefined; }
        const update = () => {
            const diff = new Date(targetDate).getTime() - Date.now();
            if (diff <= 0) { setLabel("Now"); return; }
            const mins = Math.floor(diff / 60000);
            const secs = Math.floor((diff % 60000) / 1000);
            setLabel(`${mins}m ${secs}s`);
        };
        update();
        const id = setInterval(update, 1000);
        return () => clearInterval(id);
    }, [targetDate]);
    return label;
}

/**
 * The "thinking" / "draft preview" card that appears inline at the bottom
 * of the message thread. Two states:
 *
 * 1. **Thinking** — bouncing purple dots + pulsing "Thinking" label
 * 2. **Ready** — italic draft text + live countdown + Send Now / Cancel
 */
export function RonaldPendingBubble({task, busy, onSendNow, onCancel}: {
    task: RonaldTask;
    busy?: boolean;
    onSendNow?: () => void;
    onCancel?: () => void;
}) {
    const isThinking = !task.planned_content;
    const countdown = useCountdown(isThinking ? null : (task.scheduled_at || null));
    const channel = (task.channel || "SMS").toUpperCase();

    return (
        <Box sx={{display: "flex", justifyContent: "flex-end", my: 0.5}}>
            <Box
                sx={{
                    maxWidth: 520,
                    width: "100%",
                    borderRadius: "lg",
                    p: 1.5,
                    border: "2px dashed",
                    borderColor: "#c4b5fd",
                    bgcolor: "rgba(243,232,255,0.5)",
                    boxShadow: "sm",
                }}
            >
                {/* Header row */}
                <Box sx={{display: "flex", alignItems: "center", gap: 0.75, mb: 1}}>
                    <SmartToyRoundedIcon sx={{fontSize: 16, color: "#9333ea"}} aria-hidden/>
                    <Typography
                        level="body-xs"
                        sx={{
                            fontWeight: 800,
                            background: "linear-gradient(90deg, #4f46e5, #9333ea)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                        }}
                    >
                        Ronald
                    </Typography>
                    <Box sx={{flex: 1}}/>
                    {isThinking ? (
                        <Box
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.5,
                                bgcolor: "#f1f5f9",
                                borderRadius: "9999px",
                                px: 1,
                                py: 0.25,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 6, height: 6, borderRadius: "50%",
                                    bgcolor: "#a78bfa",
                                    animation: `${bounce} 1.4s infinite ease-in-out`,
                                }}
                            />
                            <Typography level="body-xs" sx={{fontWeight: 500, color: "#64748b", fontSize: "10px"}}>
                                Thinking
                            </Typography>
                        </Box>
                    ) : countdown ? (
                        <Box
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                bgcolor: "#ede9fe",
                                borderRadius: "9999px",
                                px: 1,
                                py: 0.25,
                            }}
                        >
                            <Typography level="body-xs" sx={{fontWeight: 500, color: "#7c3aed", fontSize: "10px"}}>
                                Sending in {countdown}
                            </Typography>
                        </Box>
                    ) : null}
                </Box>

                {/* Body */}
                {isThinking ? (
                    <Box sx={{display: "flex", alignItems: "center", gap: 0.75, py: 0.5}}>
                        {[0, 150, 300].map((delay) => (
                            <Box
                                key={delay}
                                sx={{
                                    width: 8, height: 8, borderRadius: "50%",
                                    bgcolor: "#c4b5fd",
                                    animation: `${bounce} 1.4s infinite ease-in-out`,
                                    animationDelay: `${delay}ms`,
                                }}
                            />
                        ))}
                    </Box>
                ) : (
                    <Typography
                        level="body-sm"
                        sx={{
                            whiteSpace: "pre-wrap",
                            color: "#475569",
                            fontStyle: "italic",
                        }}
                    >
                        {task.planned_content}
                    </Typography>
                )}

                {/* Pending action preview */}
                {task.pending_action && (
                    <Box
                        sx={{
                            mt: 0.75,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            borderRadius: "9999px",
                            bgcolor: "#faf5ff",
                            border: "1px solid #d8b4fe",
                            px: 1,
                            py: 0.25,
                        }}
                    >
                        <Typography level="body-xs" sx={{fontWeight: 700, color: "#7c3aed", fontSize: "10px"}}>
                            ⚡ Will {task.pending_action.action === "cancel_booking" ? "cancel booking" : "flag for rescheduling"}
                        </Typography>
                        {task.pending_action.reason && (
                            <Typography level="body-xs" sx={{color: "#8b5cf6", fontSize: "10px"}}>
                                — {task.pending_action.reason}
                            </Typography>
                        )}
                    </Box>
                )}

                {/* Footer */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mt: 1,
                        pt: 1,
                        borderTop: "1px solid",
                        borderColor: "#ddd6fe",
                    }}
                >
                    {!isThinking && onSendNow && (
                        <Button
                            size="sm"
                            variant="solid"
                            disabled={!!busy}
                            onClick={onSendNow}
                            startDecorator={<PlayArrowRoundedIcon sx={{fontSize: 14}}/>}
                            sx={{
                                bgcolor: "#4f46e5",
                                "&:hover": {bgcolor: "#4338ca"},
                                fontSize: "10px",
                                fontWeight: 600,
                                px: 1.25,
                                py: 0.5,
                                minHeight: 0,
                                borderRadius: "sm",
                            }}
                        >
                            Send Now
                        </Button>
                    )}
                    {onCancel && (
                        <Button
                            size="sm"
                            variant="soft"
                            color="neutral"
                            disabled={!!busy}
                            onClick={onCancel}
                            startDecorator={<CancelRoundedIcon sx={{fontSize: 14}}/>}
                            sx={{
                                fontSize: "10px",
                                fontWeight: 600,
                                px: 1.25,
                                py: 0.5,
                                minHeight: 0,
                                borderRadius: "sm",
                            }}
                        >
                            Cancel
                        </Button>
                    )}
                    <Box sx={{flex: 1}}/>
                    <Typography level="body-xs" sx={{color: "#94a3b8", fontSize: "10px"}}>
                        {channel}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}
